import { useCallback, useEffect } from 'react';
import { formatShortcut, getModifierKeyLabel, isMac } from 'utils/platformUtils';

// Re-export platform utils for backward compatibility
export { formatShortcut, getModifierKeyLabel, isMac };

type ShortcutOptions = {
  key: string;
  cmdOrCtrl?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
};

/**
 * Hook to handle keyboard shortcuts
 *
 * @param options - Shortcut configuration
 * @param options.key - The key to listen for
 * @param options.cmdOrCtrl - If true, requires Cmd (Mac) or Ctrl (Windows/Linux)
 * @param options.ctrl - If true, requires Ctrl key
 * @param options.shift - If true, requires Shift key
 * @param options.alt - If true, requires Alt key
 * @param callback - Function to call when shortcut is triggered
 */
export const useKeyboardShortcut = (options: ShortcutOptions, callback: () => void) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const { key, cmdOrCtrl, ctrl, shift, alt } = options;

      // Check modifier keys
      let modifierMatch: boolean;
      if (ctrl) {
        modifierMatch = e.ctrlKey && !e.metaKey;
      } else if (cmdOrCtrl) {
        modifierMatch = e.metaKey || e.ctrlKey;
      } else {
        modifierMatch = !e.metaKey && !e.ctrlKey;
      }

      const shiftMatch = shift ? e.shiftKey : !e.shiftKey;
      const altMatch = alt ? e.altKey : !e.altKey;
      const keyMatch = e.key.toLowerCase() === key.toLowerCase();

      // Don't trigger in input fields or textareas, unless it's a ctrl/cmd shortcut
      // (Ctrl/Cmd shortcuts should work even when typing, as they are intentional app shortcuts)
      const activeElement = document.activeElement;
      const isInInput =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true';
      const hasModifier = ctrl || cmdOrCtrl;

      if (isInInput && !hasModifier) return;

      if (keyMatch && modifierMatch && shiftMatch && altMatch) {
        e.preventDefault();
        callback();
      }
    },
    [options, callback]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};
